import Image from "next/image";
import { Typography, Avatar, Divider, Input, Skeleton, Empty } from "antd";
import { useState, useEffect, useRef, useCallback } from "react";
import useMyQuery from "@/hooks/useMyQuery";
import debounce from "lodash/debounce";
import type { CurrencyIF} from "@/helpers";
import { formatThousands } from "@/helpers";
import { useCurrencyContext } from "@/context/CurrencyContext";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { SearchOutlined } from "@ant-design/icons";
import WhaleIcon from "@/asset/images/whale.svg";
import PigIcon from "@/asset/images/bender.svg";
import { useLanguageContext } from "@/context/LanguageContext";

dayjs.extend(relativeTime);

interface Transfer {
  hash: string;
  created_at: string;
  from: string;
  to: string;
  logo_uri: string;
  name: string;
  symbol: string;
  time: string;
  token_symbol: string;
  value: number;
  value_usd: number;
}

interface TransferResponse {
  success: boolean;
  data: {
    total: number;
    result: Transfer[];
  };
}

const shortAddress = (address: string) => {
  if (!address) return "";
  if (address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

interface TransferHeaderProps {
  transfer: Transfer;
  isDeposit: boolean;
  currentCurrency: CurrencyIF;
}

const TransferHeader = ({ transfer, currentCurrency }: TransferHeaderProps) => {
  const { language } = useLanguageContext();
  return (
    <div className="flex justify-between items-start">
      <div
        className="flex items-center gap-3 cursor-pointer"
        onClick={() => window.open(`/${language}/markets/${transfer.symbol}`, "_blank")}
      >
        <Avatar src={transfer.logo_uri} size={32} className="p-1" />
        <div className="flex flex-col gap-1">
          <Typography.Text strong className="text-base">
            {transfer.symbol}
          </Typography.Text>
          <Typography.Text type="secondary" className="text-xs">
            {dayjs(transfer.time).fromNow()}
          </Typography.Text>
        </div>
      </div>
      <div className="text-right flex flex-col gap-1">
        <Typography.Text strong className="text-base">
          {formatThousands(transfer.value_usd, currentCurrency)}
        </Typography.Text>
        <Typography.Text type="secondary" className="text-xs">
          {formatThousands(transfer.value)} {transfer.symbol}
        </Typography.Text>
      </div>
    </div>
  );
};

interface TransferFooterProps {
  transfer: Transfer;
  isDeposit: boolean;
  isLargeTransfer: boolean;
}

const TransferFooter = ({ transfer, isDeposit, isLargeTransfer }: TransferFooterProps) => (
  <div className="flex justify-between items-center mt-2">
    <div
      className="flex gap-1 cursor-pointer"
      onClick={() => window.open(`https://etherscan.io/tx/${transfer.hash}`, "_blank")}
    >
      <Typography.Text type="secondary" strong>
        {isDeposit ? "Deposit" : "Withdraw"}
      </Typography.Text>
      <Typography.Text type="secondary">
        {isDeposit ? "from " : "to "}
        {shortAddress(isDeposit ? transfer.from : transfer.to)} {isDeposit ? " to " : " from "}{" "}
        {shortAddress(isDeposit ? transfer.to : transfer.from)}
      </Typography.Text>
    </div>
    <div className="w-6 h-6 relative" title={isLargeTransfer ? "Dolphin" : "Shark"}>
      <Image src={isLargeTransfer ? WhaleIcon : PigIcon} alt="transfer-icon" fill className="object-contain" />
    </div>
  </div>
);

const TransferItem = ({ transfer }: { transfer: Transfer }) => {
  const { currentCurrency } = useCurrencyContext();
  const isDeposit = transfer.from.slice(0, 2) === "0x";
  const isLargeTransfer = transfer.value_usd > 10000000;

  return (
    <div
      className={`border-l-4 ${
        isDeposit ? "border-l-[#A4CF5E]" : "border-l-[#F45B5B]"
      } rounded-lg mb-2 bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]`}
    >
      <div className="p-4 hover:bg-gray-50 transition-colors rounded-lg">
        <TransferHeader transfer={transfer} isDeposit={isDeposit} currentCurrency={currentCurrency} />
        <Divider className="!my-2" />
        <TransferFooter transfer={transfer} isDeposit={isDeposit} isLargeTransfer={isLargeTransfer} />
      </div>
    </div>
  );
};

const TransferSkeleton = () => (
  <div className="border-l-4 border-l-gray-200 rounded-lg mb-2 bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
    <div className="p-4">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-3">
          <Skeleton.Avatar active size={32} />
          <div className="flex flex-col gap-1">
            <Skeleton.Button active size="small" style={{ width: 60 }} />
            <Skeleton.Button active size="small" style={{ width: 100 }} />
          </div>
        </div>
        <div className="text-right flex flex-col gap-1">
          <Skeleton.Button active size="small" style={{ width: 80 }} />
          <Skeleton.Button active size="small" style={{ width: 120 }} />
        </div>
      </div>
      <Divider className="!my-2" />
      <div className="flex justify-between items-center mt-2">
        <div className="flex gap-2">
          <Skeleton.Button active size="small" style={{ width: 200 }} />
        </div>
        <Skeleton.Avatar active size={24} />
      </div>
    </div>
  </div>
);

export default function TokenTransfers() {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Transfer[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useMyQuery<TransferResponse>(
    {
      url: "/v1/dex-rating/cex/transfers",
      params: { q: searchTerm, page, limit: 10 },
      includeParamsInQueryKey: true,
    },
    {
      queryKey: ["token-transfers", searchTerm, page],
      refetchInterval: 15 * 60 * 1000,
    },
  );

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      setItems([]);
      setPage(1);
    }, 300),
    [],
  );

  useEffect(() => {
    if (data?.data.result) {
      setItems(prev => (page === 1 ? data.data.result : [...prev, ...data.data.result]));
    }
  }, [data?.data.result, page]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50 && !isLoading && data?.data.result.length === 10) {
        setPage(prev => prev + 1);
      }
    };

    const container = containerRef.current;
    container?.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, [isLoading, data?.data.result.length]);

  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] p-4">
      <Typography.Title level={5} className="!mb-4">
        CEX Transfers
      </Typography.Title>
      <Input
        placeholder="Search for CEX"
        prefix={<SearchOutlined className="text-gray-400" />}
        onChange={e => handleSearch(e.target.value)}
        className="mb-4"
      />
      <div ref={containerRef} className="space-y-4 max-h-[400px] overflow-auto custom-scrollbar">
        {isLoading && page === 1 ? (
          <>
            <TransferSkeleton />
            <TransferSkeleton />
            <TransferSkeleton />
          </>
        ) : !items.length ? (
          <div className="py-8">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Typography.Text type="secondary">
                  {searchTerm ? "No results found" : "No transfers yet"}
                </Typography.Text>
              }
            />
          </div>
        ) : (
          items.map((transfer, index) => <TransferItem key={index} transfer={transfer} />)
        )}
        {isLoading && page > 1 && (
          <div className="py-4">
            <TransferSkeleton />
          </div>
        )}
      </div>
    </div>
  );
}

